import React, { useState } from 'react'
import { format } from 'date-fns'
import { PageContent } from '../../components/Layout'
import { ReportsIcon } from '../../components/Icons'
import { ReportSidebar } from './ReportSidebar'
import { ReportConfigPanel } from './ReportConfigPanel'
import { ReportTable } from './ReportTable'
import { useReportData } from './useReportData'
import { CATEGORIES, REPORTS } from './reportDefinitions'
import type { Report, DateRange } from './types'

const Reports: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedReport, setSelectedReport] = useState<Report | null>(null)
  const [dateRange, setDateRange] = useState<DateRange>({
    start: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  })
  const [filters, setFilters] = useState<Record<string, string>>({})

  const { reportData, loading, error, loadReport, exportToCSV, exportToPDF, clearData } = useReportData()

  const handleSelectReport = (report: Report) => {
    setSelectedReport(report)
    clearData()
  }

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  return (
    <PageContent>
      <div className="space-y-6">
        {/* Page header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <ReportsIcon size={32} />
            Reports
          </h1>
          <p className="mt-1 text-gray-500 dark:text-gray-400">
            Generate, filter, and export your brokerage reports.
          </p>
        </div>

        {/* Layout: sidebar + main */}
        <div className="flex flex-col lg:flex-row gap-6 items-start">
          <ReportSidebar
            categories={CATEGORIES}
            reports={REPORTS}
            selectedCategory={selectedCategory}
            selectedReport={selectedReport}
            onSelectCategory={setSelectedCategory}
            onSelectReport={handleSelectReport}
          />

          {/* Main panel */}
          <div className="flex-1 min-w-0 space-y-4">
            {selectedReport ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 space-y-6">
                {/* Report title */}
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {selectedReport.name}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {selectedReport.description}
                  </p>
                </div>

                {/* Config: date range, filters, generate */}
                <ReportConfigPanel
                  report={selectedReport}
                  dateRange={dateRange}
                  filters={filters}
                  loading={loading}
                  onDateRangeChange={setDateRange}
                  onFilterChange={handleFilterChange}
                  onGenerate={() => loadReport(selectedReport, dateRange, filters)}
                />

                {/* Error */}
                {error && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400 text-sm">
                    {error}
                  </div>
                )}

                {/* Results */}
                {reportData && !loading && (
                  <ReportTable
                    data={reportData}
                    onExportCSV={() => exportToCSV(selectedReport)}
                    onExportPDF={() => exportToPDF(selectedReport, dateRange)}
                  />
                )}
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-16 text-center">
                <ReportsIcon size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  Select a Report
                </h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  Choose a report from the sidebar to get started.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageContent>
  )
}

export default Reports
